// import { ArrowLeftIcon } from "@heroicons/react/24/outline";
// import { Button } from "components/Button";
// import CustomIcon from "components/CustomIcon";
import { Input } from "components/ResumeForm/Form/InputGroup";
import RichEditorWithSuggestions from "components/ResumeForm/Form/RichEditorWithSuggestions";
import { WorkExperiencesForm } from "components/ResumeForm/WorkExperiencesForm";
import { CreateHandleChangeArgsWithDescriptions } from "components/ResumeForm/types";
import { useAppSelector } from "lib/redux/hooks";
import {
  changeWorkExperiences,
  selectWorkExperiences,
} from "lib/redux/resumeSlice";
import { ResumeWorkExperience } from "lib/redux/types";
import { calculateWordCount } from "lib/resume-scoring/content-details-sections/word-count";
import {
  IndividualWorkExperiencesAnalysis,
  ResumeAnalysis,
} from "lib/resume-scoring/resume-analysis/resume-analyse-types";
import Image from "next/image";
import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { getDateIssue } from "score-check/utils/getDateIssue";
import { checkDateStructureConsistent } from "score-check/utils/isDateStructureConsistent";
import { DateStructureConsistent } from "../common/DateStructureNotConsistent";
import { NoIssueBox } from "../common/NoIssueBox";
import { ShortDescription } from "../common/ShortDescription";
import { PassiveVoice } from "../common/PassiveVoice";
import { LongSentences } from "../common/LongSentences";
import { DoesNotEndsWithPeriod } from "../common/DoesNotEndsWithPeriod";
import { PersonalPronouns } from "../common/PersonalPronouns";
import { SectionHeader } from "../common/SectionHeader";
// import { MistakesSection } from "../common/MistakesSection";
import { WordMistakesSection } from "../common/WordMistakesSection";
import { FixButtons } from "../common/FixButtons";
import { DateInputSection } from "../common/DateInputSection";
import { TextInputSection } from "../common/TextInputSection";
import { AddMoreSection } from "../common/AddMoreSection";
import { ImpactfulStatementWarning } from "../common/ImpactfulStatementWarning";
import { ReverseOrderWarning } from "../common/ReverseOrderWarning";
import { EmploymentGapWarning } from "../common/EmploymentGapWarning";
import { EmploymentOverlapWarning } from "../common/EmploymentOverlapWarning";
import { checkReverseOrder } from "score-check/utils/checkReverseOrder";
import { WorkExperienceScoreAnalysis } from "lib/resume-scoring/types";
// import { OverallAnalysis } from "lib/resume-scoring/resume-analysis/resume-analyse-types";
import { highlightSection, clearHighlight } from "lib/redux/highlightSlice";
import { MistakesDisplayV2 } from "../common/MistakesDisplayV2";
import { GrammarMistakesSection } from "../common/GrammarMistakes";

export const ExperienceSection = ({
  score,
  maxScore,
  analysis,
}: {
  score: WorkExperienceScoreAnalysis;
  maxScore: number;
  analysis: ResumeAnalysis["workExperiences"];
}) => {
  const workExperiences = useAppSelector(selectWorkExperiences);

  const experienceInReverseOrder = !checkReverseOrder(
    analysis.individualWorkExperiencesAnalysis
  );

  const hasEmploymentGap = score.gap > 0;
  const hasEmploymentOverlap = score.overlap > 0;

  const [isAddExperienceOpen, setIsAddExperienceOpn] = useState(false);

  return (
    <div className="scrollbar-hide scrollbar-thumb-gray-200 relative mb-4 flex h-full max-h-[88.5vh] flex-col rounded-[12px] bg-white p-1 shadow-md lg:rounded-[6px]">
      {/* Fixed Sticky Header */}
      <SectionHeader
        title="Experience"
        score={Math.floor(score.totalScore)}
        maxScore={maxScore}
      />

      {/* Scrollable Content - Hidden Scrollbar */}
      <div className="scrollbar-hide flex flex-1 flex-col gap-[25px] overflow-auto px-0 pb-3 lg:gap-[30px] lg:px-0 xl:gap-[40px]">
        {analysis.individualWorkExperiencesAnalysis.map((exp, index) => (
          <IndividualExperience
            key={index}
            exp={exp}
            index={index}
            workExperiences={workExperiences}
          />
        ))}
        {experienceInReverseOrder && (
          <ReverseOrderWarning sectionName="Experience" />
        )}
        {hasEmploymentGap && <EmploymentGapWarning />}
        {hasEmploymentOverlap && <EmploymentOverlapWarning />}
        <AddMoreSection
          isOpen={isAddExperienceOpen}
          toggleOpen={() => setIsAddExperienceOpn(!isAddExperienceOpen)}
          label="Experience"
        />
        {isAddExperienceOpen && (
          // <div>
            <WorkExperiencesForm />
            // <SaveButton isAddOpen={isAddExperienceOpen} />
          // </div>
        )}
      </div>

      {/* Add custom scrollbar hide styles */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

const IndividualExperience = ({
  exp,
  index,
  workExperiences,
}: {
  exp: IndividualWorkExperiencesAnalysis;
  index: number;
  workExperiences: ResumeWorkExperience[];
}) => {
  const isDateIssue = !exp.startDate || !exp.endDate;

  // Collect and sanitize grammar mistakes so falsy/null values don't count
  // Deduplicate to show each unique mistake only once
  const grammarMistakes = [
    ...new Set(
      exp.descriptionAnalysis
        .flatMap((point) =>
          point.analysis.grammarMistakes.map((m) => m.incorrect)
        )
        .filter(Boolean) as string[]
    ),
  ];

  const hasGrammarMistakes = grammarMistakes.length > 0;
  const hasSpellingMistakes = exp.descriptionAnalysis.some(
    (desc) =>
      desc.analysis.spellingMistakes &&
      desc.analysis.spellingMistakes.length > 0
  );

  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);

  const sectionRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useDispatch();

  const handleWorkExperienceChange = (
    ...[
      field,
      value,
    ]: CreateHandleChangeArgsWithDescriptions<ResumeWorkExperience>
  ) => {
    dispatch(changeWorkExperiences({ idx: index, field, value } as any));
  };

  // Get an array of non-past tense verbs that are the first word of a sentence
  // Deduplicate to show each unique verb only once
  const actionVerbIssues = [
    ...new Set(
      exp.descriptionAnalysis.flatMap((desc) => {
        return desc.analysis.actionVerbAnalysis
          .filter((verb) => {
            // Check if the verb is not in past tense
            if (verb.isPastTense !== 0) return false;

            // Get the verb position in the statement
            const statement = desc.statement.trim();
            const firstWord = statement
              .split(" ")[0]
              .toLowerCase()
              .replace(/[^\w]/g, "");

            // Only include if it's the first word of the sentence
            return firstWord === verb.verb.toLowerCase();
          })
          .map((verb) => verb.verb);
      })
    ),
  ];

  const hasActionVerbIssue = actionVerbIssues.length > 0;
  // const hasBuzzWordsIssue = exp.descriptionAnalysis.some(
  //   (desc) => desc.analysis.buzzWords.length > 0
  // );
  // const hasFillerWordsIssue = exp.descriptionAnalysis.some(
  //   (desc) => desc.analysis.fillerWords.length > 0
  // );
  const hasPassiveVoiceIssue = exp.descriptionAnalysis.some(
    (desc) => desc.analysis.isPassiveVoice == 1
  );

  const hasImpactifulStatement = exp.descriptionAnalysis.some((desc) => {
    return desc.analysis.isQuantified == 1;
  });

  const getHasDescriptionIssues = (): boolean => {
    let has = false;
    exp.descriptionAnalysis.map((desc) => {
      if (hasActionVerbIssue) has = true;
      if (desc.analysis.buzzWords.length > 0) has = true;
      if (desc.analysis.fillerWords.length > 0) has = true;
      if (desc.analysis.grammarMistakes.length > 0) has = true;
      if (desc.analysis.spellingMistakes.length > 0) has = true;
      if (desc.analysis.isPassiveVoice == 1) has = true;
    });
    // if (exp.descriptionAnalysis.length < 2) has = true;
    // if (!hasImpactifulStatement) has = true;
    return has;
  };

  // Deduplicate sentences (unlikely but possible to have exact duplicates)
  const longSentences = [
    ...new Set(
      exp.descriptionAnalysis
        .filter((desc) => calculateWordCount(desc.statement) > 25)
        .map((desc) => desc.statement)
    ),
  ];

  const doesNotEndsWithPeriod = [
    ...new Set(
      exp.descriptionAnalysis
        .filter((desc) => desc.analysis.endsWithPeriod == 0)
        .map((desc) => desc.statement)
    ),
  ];

  // Deduplicate personal pronouns - show each unique pronoun only once
  const personalPronouns = [
    ...new Set(
      exp.descriptionAnalysis
        .filter((desc) => desc.analysis.personalPronouns.length > 0)
        .map((desc) => desc.analysis.personalPronouns)
        .flat()
    ),
  ];

  const hasDescriptionIssues = getHasDescriptionIssues();
  const noDescription = exp.descriptionAnalysis.length === 0;

  const isShortDescription = exp.descriptionAnalysis.length < 2;

  const dateIssueText = getDateIssue(exp.startDate, exp.endDate);
  const isDateStructureConsistent = checkDateStructureConsistent(
    exp.startDateEntry,
    exp.endDateEntry
  );

  // Section-level highlighting handlers
  const handleSectionMouseEnter = () => {
    dispatch(
      highlightSection({
        section: "experience",
        itemIndex: index,
      })
    );
  };

  const handleSectionMouseLeave = () => {
    dispatch(clearHighlight());
  };

  // Local repeated action verbs for this experience (derived from descriptionAnalysis)
  const localRepeatedActionVerbs = (() => {
    const verbs: string[] = exp.descriptionAnalysis.flatMap((d) =>
      d.analysis.actionVerbAnalysis.map((v) => v.verb)
    );
    const counts: Record<string, number> = {};
    verbs.forEach((v) => {
      const key = (v || "").trim().toLowerCase();
      if (!key) return;
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.keys(counts)
      .filter((k) => counts[k] > 1)
      .map((k) => k);
  })();

  // Local repeated phrases (simple trigram-based detection across this experience statements)
  const localRepeatedPhrases = (() => {
    const shingles: string[] = [];
    const normalize = (s: string) =>
      s
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .replace(/\s+/g, " ");
    exp.descriptionAnalysis.forEach((d) => {
      const text = normalize(d.statement || "");
      if (!text) return;
      const words = text.split(" ").filter(Boolean);
      const n = 3; // trigrams
      for (let i = 0; i + n <= words.length; i++) {
        shingles.push(words.slice(i, i + n).join(" "));
      }
    });
    const counts: Record<string, number> = {};
    shingles.forEach((s) => (counts[s] = (counts[s] || 0) + 1));
    return Object.keys(counts).filter((k) => counts[k] > 1);
  })();

  // Collect data for MistakesDisplayV2
  // Filter out null/falsy values and deduplicate
  const spellingMistakes = [
    ...new Set(
      exp.descriptionAnalysis
        .flatMap((point) =>
          point.analysis.spellingMistakes.map((mistake) => mistake.incorrect)
        )
        .filter(Boolean) as string[]
    ),
  ];

  const buzzWords = [
    ...new Set(
      exp.descriptionAnalysis
        .flatMap((point) => point.analysis.buzzWords)
        .filter(Boolean) as string[]
    ),
  ];

  // Split into words and phrases
  const repeatedWords = localRepeatedActionVerbs; // Already deduplicated in the logic above
  const repeatedPhrases = localRepeatedPhrases; // Already deduplicated in the logic above

  const isMissingInfo =
    isDateIssue || !exp.company || !exp.role || noDescription;

  const showFixButton =
    isMissingInfo ||
    hasDescriptionIssues ||
    !hasImpactifulStatement ||
    doesNotEndsWithPeriod.length > 0 ||
    isShortDescription ||
    longSentences.length > 0 ||
    doesNotEndsWithPeriod.length > 0;

  return (
    <div className="mt-0 flex flex-col gap-4 rounded-2xl bg-gradient-to-br from-white to-gray-50 p-4 font-montserrat shadow-sm transition-shadow hover:shadow-md lg:gap-6 lg:p-2 lg:pt-0 xl:gap-8 xl:p-4 xl:pt-0">
      <span
        className="sticky top-0 z-10 mx-auto flex cursor-pointer items-center justify-center gap-3 rounded-xl bg-gray-100 px-[16px] py-[4px] text-center text-sm font-semibold text-black shadow-md transition-opacity hover:opacity-90 md:text-lg lg:px-[20px] lg:py-[8px] lg:text-xl xl:px-[24px] xl:py-[8px]"
        onMouseEnter={handleSectionMouseEnter}
        onMouseLeave={handleSectionMouseLeave}
      >
        Experience {index + 1}
      </span>

      {/* Missing Information*/}
      {isMissingInfo && (
        <div className="relative mt-3 xl:mt-1 flex flex-col gap-[10px] rounded-xl bg-[#FEE7E4] px-[15px] py-[10px]  lg:gap-[16px] lg:px-[20px] lg:py-[14px]">
          <div className="absolute left-4 top-[-16px] flex items-center gap-2 rounded-xl bg-white px-4 py-2 font-bold text-black shadow-md lg:left-8 lg:px-6">
            <Image
              src="/icons/score-check/missing.svg"
              height={24}
              width={24}
              alt="icon"
              className="h-4 w-4 lg:h-5 lg:w-5"
            />
            <span className="text-sm font-bold lg:text-base xl:text-lg">
              Missing Information
            </span>
          </div>

          <div className="mt-6">
            <div className=" flex flex-col gap-2">
              {/* Missing Dates */}
              {isDateIssue && (
                <DateInputSection
                  startDate={workExperiences[index].startDate}
                  endDate={workExperiences[index].endDate}
                  dateIssueText={dateIssueText}
                  handleChange={(field, value) =>
                    handleWorkExperienceChange(field, value)
                  }
                />
              )}

              {/* Missing Company */}
              {!exp.company && (
                <TextInputSection
                  value={workExperiences[index].company}
                  placeholder="Company Name"
                  fieldName="company"
                  missingText="Company name missing"
                  handleChange={(field, value) =>
                    handleWorkExperienceChange(field, value)
                  }
                  icon="duration"
                />
              )}

              {/* Missing Job Title */}
              {!exp.role && (
                <TextInputSection
                  value={workExperiences[index].jobTitle}
                  placeholder="Analyst"
                  fieldName="jobTitle"
                  missingText="Job title missing"
                  handleChange={(field, value) =>
                    handleWorkExperienceChange(field, value)
                  }
                  icon="company"
                />
              )}

              {noDescription && (
                <div className="flex flex-col items-center justify-between rounded-md border-[3px] border-white bg-[#F8F8F8] px-[12px] py-[16px] sm:rounded-lg sm:px-[36px] sm:py-[16px] lg:flex-row lg:rounded-md lg:px-[6px] lg:py-[16px] xl:rounded-xl xl:px-[12px] xl:py-[28px]">
                  <div className="flex w-full items-center justify-start gap-[15px] lg:gap-[28px]">
                    <Image
                      src="/icons/warningRed.svg"
                      width={20}
                      height={20}
                      alt="warning"
                      className="h-4 w-4 lg:h-6 lg:w-6"
                    />
                    <span className="font-montserrat text-[12px] font-medium text-red-700 sm:text-base md:text-lg lg:text-base">
                      No Description found
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 flex flex-col items-center justify-center gap-3">
              <div className="relative h-32 w-32 lg:h-40 lg:w-40">
                <Image
                  src="/assets/resumeScore/missingInfo.png"
                  alt="Long sentences illustration"
                  fill
                  className="object-contain"
                />
              </div>
              <p className="text-center text-sm font-semibold text-red-600 lg:text-base">
                Missing information can weaken your resume
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Mistakes Section */}
      {/* {hasDescriptionIssues && (
        <MistakesSection 
          section="experience"
          itemIndex={index}
          mistakes={exp.descriptionAnalysis
            .filter(point => {
              // Only include statements that have at least one issue
              return point.analysis.grammarMistakes.length > 0 ||
                     point.analysis.spellingMistakes.length > 0 ||
                     point.analysis.buzzWords.length > 0 ||
                     point.analysis.fillerWords.length > 0 ||
                     point.analysis.isPassiveVoice === 1 ||
                     point.analysis.personalPronouns.length > 0 ||
                     (point.analysis.actionVerbAnalysis.length > 0 && 
                      point.analysis.actionVerbAnalysis.some(verb => verb.isPastTense === 0)) ||
                     point.analysis.endsWithPeriod === 0 ||
                     calculateWordCount(point.statement) > 25;
            })
            .map(point => point.statement)}
        />
      )} */}

      {/* Spelling, Buzz Words, Repeated Words */}
      <MistakesDisplayV2
        spellingMistakes={spellingMistakes}
        buzzWords={buzzWords}
        repeatedWords={repeatedWords}
        section="experience"
        itemIndex={index}
      />

      {/* Grammar Section */}
      {hasGrammarMistakes && (
        <GrammarMistakesSection
          title="Grammar mistake"
          icon="grammarMistake"
          section="experience"
          itemIndex={index}
          mistakes={grammarMistakes}
        />
      )}

      {personalPronouns.length > 0 && (
        <PersonalPronouns
          personalPronouns={personalPronouns}
          section="experience"
          itemIndex={index}
        />
      )}

      {/* Repeated Phrases */}
      {/* {repeatedPhrases.length > 0 && (
        <WordMistakesSection
          title="Repeated phrases"
          icon="spellingMistake"
          section="experience"
          itemIndex={index}
          mistakeType="filler"
          mistakes={repeatedPhrases}
        />
      )} */}

      {/* Weak Verbs */}
      {hasActionVerbIssue && (
        <WordMistakesSection
          title="Past tense not used"
          icon="spellingMistake"
          section="experience"
          itemIndex={index}
          mistakeType="actionVerb"
          mistakes={actionVerbIssues}
        />
      )}

      {doesNotEndsWithPeriod.length > 0 && (
        <DoesNotEndsWithPeriod
          doesNotEndsWithPeriod={doesNotEndsWithPeriod}
          section="experience"
          itemIndex={index}
        />
      )}

      {/* Passive Voice */}
      {hasPassiveVoiceIssue && (
        <div className="flex-1">
          <PassiveVoice />
        </div>
      )}

      {isShortDescription && <ShortDescription />}

      {/* Long Sentences */}
      <LongSentences
        longSentences={longSentences}
        section="experience"
        itemIndex={index}
      />

      <ImpactfulStatementWarning
        hasImpactfulStatements={hasImpactifulStatement}
      />
      {/* Fix Buttons */}
      {showFixButton && (
        <>
          {isDescriptionOpen && (
            <div className="mx-auto flex w-full flex-col items-center justify-center gap-3 rounded-xl bg-white p-4 lg:p-6">
              {/* Company Name */}
              <Input
                label="Company"
                labelClassName="w-full"
                name="company"
                placeholder="Tech Dreamers Inc."
                value={workExperiences[index].company}
                onChange={handleWorkExperienceChange}
              />

              {/* Job Title */}
              <Input
                label="Job Title"
                labelClassName="w-full"
                name="jobTitle"
                placeholder="Code Alchemist"
                value={workExperiences[index].jobTitle}
                onChange={handleWorkExperienceChange}
              />

              {/* Start Date and End Date */}
              <div className="grid w-full grid-cols-2 gap-3">
                <Input
                  label="Start Date"
                  labelClassName="w-full"
                  name="startDate"
                  placeholder="Jun 2022"
                  value={workExperiences[index].startDate}
                  onChange={handleWorkExperienceChange}
                />
                <Input
                  label="End Date"
                  labelClassName="w-full"
                  name="endDate"
                  placeholder="Present"
                  value={workExperiences[index].endDate}
                  onChange={handleWorkExperienceChange}
                />
              </div>

              {/* Description Editor */}
              <div className="w-full">
                <RichEditorWithSuggestions
                  name="descriptions"
                  value={workExperiences[index].descriptions.map(
                    (desc) => desc
                  )}
                  onChange={handleWorkExperienceChange}
                  placeholder="Work Description"
                  section="workExperiences"
                />
              </div>
            </div>
          )}
          <FixButtons
            isEditMode={isDescriptionOpen}
            toggleEditMode={() => setIsDescriptionOpen(!isDescriptionOpen)}
          />
        </>
      )}

      <DateStructureConsistent
        isDateStructureConsistent={isDateStructureConsistent}
      />
      {!showFixButton && (
        <>
          <NoIssueBox message="No issues found in this experience description. Great job!" />
        </>
      )}
    </div>
  );
};
